import { useMemo, useRef, useState } from 'react';
import type { ExportArrangement, ExportBundle, ExportFormat } from '@/core/export';
import { describeDesign } from '@/core/export';
import { designFilename } from '@/core/design/serialize';
import { layoutForDesign } from '@/core/wall';
import { useDesignStore } from '@/state/store';
import { downloadBlob, useExport } from '../hooks/useExport';
import { Section, Segmented, Slider, Toggle } from '../components/Controls';

/**
 * Step 3 — getting the files out.
 *
 * The defaults answer the common case (a whole wall as one 3MF laid out the way
 * it will hang); the alternatives cover printing one panel at a time and
 * getting parts spread across a build plate.
 */

const FORMATS = [
  { value: 'stl', label: 'STL', help: 'Universal triangle mesh. Every slicer reads it.' },
  { value: '3mf', label: '3MF', help: 'Keeps panels as named parts and records millimetres.' },
] as const;

const BUNDLES = [
  { value: 'single', label: 'One file', help: 'Everything in a single model file.' },
  { value: 'per-panel', label: 'One per panel', help: 'A zip with a file for each panel.' },
] as const;

const ARRANGEMENTS = [
  { value: 'wall', label: 'As it hangs', help: 'Panels keep their wall positions.' },
  { value: 'plate', label: 'On a plate', help: 'Panels spread out with clearance for printing.' },
] as const;

export function ExportStep() {
  const design = useDesignStore((state) => state.design);
  const selectedPanel = useDesignStore((state) => state.selectedPanel);
  const rename = useDesignStore((state) => state.rename);
  const exportDesignJson = useDesignStore((state) => state.exportDesignJson);
  const loadDesign = useDesignStore((state) => state.loadDesign);

  const { progress, run, dismissError } = useExport();
  const fileInput = useRef<HTMLInputElement>(null);

  const [format, setFormat] = useState<ExportFormat>('3mf');
  const [bundle, setBundle] = useState<ExportBundle>('single');
  const [arrangement, setArrangement] = useState<ExportArrangement>('wall');
  const [plateSpacing, setPlateSpacing] = useState(5);
  const [onlySelected, setOnlySelected] = useState(false);

  const panelCount = design.grid.columns * design.grid.rows;
  const layout = useMemo(() => layoutForDesign(design), [design]);

  // Triangles are fully determined by the resolution and the panel count, so
  // the estimate needs no mesh and updates instantly with the sliders.
  const estimate = useMemo(() => {
    const perPanelTop = design.panel.segments * design.panel.segments * 2;
    const skirt = design.panel.segments * 4 * 3;
    const tiles = onlySelected && selectedPanel !== null ? 1 : panelCount;
    // A light box is a second part for every tile, and it was not being
    // counted: the summary said six parts for a wall that exported twelve.
    const boxed = design.led.enabled && design.led.mounting === 'lightbox';
    // A channel doubles a panel's own surfaces, because its back stops being a
    // flat fan and becomes a mirror of the top.
    const channelled = design.led.enabled && design.led.mounting === 'channel';
    const perPanel = channelled ? perPanelTop * 2 + skirt : perPanelTop + skirt;
    const triangles = perPanel * tiles + (boxed ? perPanelTop * tiles : 0);
    return {
      triangles,
      megabytes: (triangles * 50) / 1e6,
      exported: tiles * (boxed ? 2 : 1),
    };
  }, [
    design.panel.segments,
    design.led.enabled,
    design.led.mounting,
    panelCount,
    onlySelected,
    selectedPanel,
  ]);

  const heavy = estimate.triangles > 3_000_000;

  return (
    <div className="step">
      <header className="step__header">
        <h2 className="step__title">Export</h2>
        <p className="step__subtitle">{describeDesign(design)}</p>
      </header>

      <div className="step__body">
        <Section title="Design name" description="Used for the exported filenames.">
          <input
            className="text-input"
            type="text"
            value={design.name}
            onChange={(event) => rename(event.target.value)}
            aria-label="Design name"
          />
        </Section>

        <Section title="Format">
          <Segmented<ExportFormat> value={format} options={FORMATS} onChange={setFormat} />
          <p className="field__help">{FORMATS.find((entry) => entry.value === format)?.help}</p>
        </Section>

        <Section title="Files">
          <Segmented<ExportBundle> value={bundle} options={BUNDLES} onChange={setBundle} />
          {bundle === 'per-panel' ? (
            <p className="field__help">
              The zip also contains a readme with the print settings and the design file itself, so
              the wall can be rebuilt later.
            </p>
          ) : null}

          <Segmented<ExportArrangement>
            value={arrangement}
            options={ARRANGEMENTS}
            onChange={setArrangement}
          />
          {arrangement === 'plate' ? (
            <Slider
              label="Clearance"
              value={plateSpacing}
              min={0}
              max={30}
              step={1}
              unit="mm"
              onChange={setPlateSpacing}
            />
          ) : null}

          {selectedPanel !== null ? (
            <Toggle
              label={`Only panel ${selectedPanel + 1}`}
              checked={onlySelected}
              help="Exports the panel selected in the viewport."
              onChange={setOnlySelected}
            />
          ) : null}
        </Section>

        <Section title="What you will get">
          <dl className="summary">
            <div>
              <dt>Parts</dt>
              <dd>{estimate.exported}</dd>
            </div>
            <div>
              <dt>Panel size</dt>
              <dd>
                {design.panel.size} × {design.panel.size} ×{' '}
                {(design.panel.baseThickness + design.panel.reliefDepth).toFixed(1)} mm
              </dd>
            </div>
            <div>
              <dt>Wall</dt>
              <dd>
                {layout.metrics.width.toFixed(0)} × {layout.metrics.height.toFixed(0)} mm
              </dd>
            </div>
            <div>
              <dt>Triangles</dt>
              <dd>≈ {estimate.triangles.toLocaleString('en-US')}</dd>
            </div>
            <div>
              <dt>File size</dt>
              <dd>
                ≈ {estimate.megabytes < 1 ? '<1' : estimate.megabytes.toFixed(0)} MB
                {format === '3mf' ? ' before compression' : ''}
              </dd>
            </div>
          </dl>

          {heavy ? (
            <p className="status status--warn">
              That is a lot of geometry. It will export, but some slicers get slow past a few
              million triangles — consider a lower mesh resolution or exporting panel by panel.
            </p>
          ) : null}
        </Section>

        {progress.error ? (
          <p className="status status--error" onClick={dismissError} role="alert">
            {progress.error}
          </p>
        ) : null}

        {progress.lastResult ? (
          <p className="status status--ok">
            Saved {progress.lastResult.filename} ·{' '}
            {progress.lastResult.stats.triangles.toLocaleString('en-US')} triangles ·{' '}
            {progress.lastResult.stats.volumeCm3.toFixed(0)} cm³ of material
          </p>
        ) : null}

        <button
          type="button"
          className="button button--primary button--block"
          disabled={progress.running}
          onClick={() =>
            run(design, {
              format,
              bundle,
              arrangement,
              plateSpacing,
              panelIndex: onlySelected && selectedPanel !== null ? selectedPanel : undefined,
            })
          }
        >
          {progress.running
            ? progress.total > 0
              ? `Building ${progress.completed}/${progress.total}…`
              : 'Building…'
            : `Export ${format.toUpperCase()}`}
        </button>

        <Section
          title="Design file"
          description="A self-contained JSON file with the layers and any imported images."
        >
          <div className="button-row">
            <button
              type="button"
              className="button"
              onClick={() =>
                downloadBlob(exportDesignJson(), designFilename(design), 'application/json')
              }
            >
              Save design
            </button>
            <button type="button" className="button" onClick={() => fileInput.current?.click()}>
              Open design
            </button>
            <input
              ref={fileInput}
              type="file"
              accept=".json,application/json"
              hidden
              onChange={async (event) => {
                const file = event.target.files?.[0];
                event.target.value = '';
                if (file) loadDesign(await file.text());
              }}
            />
          </div>
        </Section>
      </div>
    </div>
  );
}
